import { useCallback, useMemo } from 'react';
import { useHouseDocumentsStatus } from '@/hooks/documents/useHouseDocumentsStatus.ts';
import { DocumentStatusV2Schema, PipelineStatusSchema } from '@/client/types.gen.ts';
import {
	isEnrichmentPathInFlight,
	isKbPathInFlight,
	isRunCompleted,
	isRunFailed,
	isRunProcessing,
} from '@/utils/pipelineStatusUtils.ts';

/**
 * Aggregate statistics for house document pipeline statuses. See ./PIPELINE_STATUS.md for the DAG.
 *
 * Two flags cover the two distinct async concerns in the V2 DAG, plus a convenience union:
 * - isUpdatingKnowledgeBase: KB path (bnd_v2 -> metadata_profiling -> metadata_extraction -> kbs) running
 *   - documents not yet searchable in chat
 * - isEnrichingDocuments: document_enrichment running - tags/summaries not applied yet
 * - isProcessingPipeline: derived convenience flag - true if either of the above is true
 */
export type HouseDocumentAggregates = {
	total_docs: number;
	any_failed: boolean;
	all_completed: boolean;
	/** True while the KB path (bnd_v2 -> metadata_profiling -> metadata_extraction -> kbs) is running - not yet queryable */
	isUpdatingKnowledgeBase: boolean;
	/** True while document_enrichment is running - tags/summaries may be stale */
	isEnrichingDocuments: boolean;
	/** True if any pipeline (KB or enrichment) is still running */
	isProcessingPipeline: boolean;

	bnd: PipelineStats;
	metadata: PipelineStats;
	kbs: PipelineStats;
	documentEnrichment: PipelineStats;
};

export interface PipelineStats {
	completed: number;
	failed: number;
	in_progress: number;
	pending: number;
}

function createPipelineStats(): PipelineStats {
	return { completed: 0, failed: 0, in_progress: 0, pending: 0 };
}

function countRun(stats: PipelineStats, run?: PipelineStatusSchema | null) {
	if (isRunCompleted(run)) stats.completed++;
	else if (isRunFailed(run)) stats.failed++;
	else if (isRunProcessing(run)) stats.in_progress++;
	else stats.pending++;
}

/**
 * House-level document status stream with O(1) lookups by document id.
 * Wraps useHouseDocumentsStatus and exposes a map, helpers, and aggregates.
 * V2 pipeline DAG (see ./PIPELINE_STATUS.md):
 *   bnd_v2 -> metadata_profiling -> metadata_extraction -> kbs, with document_enrichment forking after profiling.
 */
export function useHouseRecordStatus(houseId: number | null | undefined) {
	// Continue polling documents - no need to stop based on setup_completed.
	const query = useHouseDocumentsStatus(houseId, true);

	const { statusById, aggregates } = useMemo(() => {
		const list = query.data?.documents ?? [];
		const statusById: Record<number, DocumentStatusV2Schema> = {};
		const total = list.length;
		const bnd = createPipelineStats();
		const metadataProfiling = createPipelineStats();
		const metadata = createPipelineStats();
		const kbs = createPipelineStats();
		const documentEnrichment = createPipelineStats();
		let isUpdatingKnowledgeBase = false;
		let isEnrichingDocuments = false;

		for (const doc of list) {
			statusById[doc.id] = doc;
			countRun(bnd, doc.bnd_v2);
			countRun(metadataProfiling, doc.metadata_profiling);
			countRun(metadata, doc.metadata_extraction);
			countRun(kbs, doc.kbs);
			countRun(documentEnrichment, doc.document_enrichment);

			// Terminal-based so the UI does not flicker in pipeline handoff gaps.
			if (!isUpdatingKnowledgeBase && isKbPathInFlight(doc)) {
				isUpdatingKnowledgeBase = true;
			}
			if (!isEnrichingDocuments && isEnrichmentPathInFlight(doc)) {
				isEnrichingDocuments = true;
			}
		}

		const any_failed =
			bnd.failed > 0 ||
			metadataProfiling.failed > 0 ||
			metadata.failed > 0 ||
			kbs.failed > 0 ||
			documentEnrichment.failed > 0;
		const all_completed = total > 0 && kbs.completed === total;
		const isProcessingPipeline = isUpdatingKnowledgeBase || isEnrichingDocuments;
		const aggregates: HouseDocumentAggregates = {
			total_docs: total,
			any_failed,
			all_completed,
			isUpdatingKnowledgeBase,
			isEnrichingDocuments,
			isProcessingPipeline,
			bnd,
			metadata,
			kbs,
			documentEnrichment,
		};

		return { statusById, aggregates };
	}, [query.data]);

	const getStatusById = useCallback(
		(id?: number | null): DocumentStatusV2Schema | undefined => (id ? statusById[id] : undefined),
		[statusById],
	);

	const getKbs = useCallback(
		(id?: number | null): PipelineStatusSchema | null | undefined => getStatusById(id)?.kbs,
		[getStatusById],
	);

	// A document is still processing while either path (KB or tag/summary) is in flight.
	const isProcessing = useCallback(
		(id?: number | null) => {
			const doc = getStatusById(id);
			return isKbPathInFlight(doc) || isEnrichmentPathInFlight(doc);
		},
		[getStatusById],
	);

	// A document is ready when kbs (terminal node) is completed.
	const isReady = useCallback((id?: number | null) => isRunCompleted(getKbs(id)), [getKbs]);

	const isFailed = useCallback(
		(id?: number | null) => {
			const doc = getStatusById(id);
			return (
				isRunFailed(doc?.bnd_v2) ||
				isRunFailed(doc?.metadata_profiling) ||
				isRunFailed(doc?.metadata_extraction) ||
				isRunFailed(doc?.kbs) ||
				isRunFailed(doc?.document_enrichment)
			);
		},
		[getStatusById],
	);

	return {
		...query,
		statusById,
		aggregates,
		getStatusById,
		getKbs,
		isProcessing,
		isReady,
		isFailed,
	} as const;
}
